import React, {useEffect, useMemo, useRef, useState} from 'react';
import "/node_modules/flag-icons/css/flag-icons.min.css";
import { getServers } from '../lib/api';


const PAGE_SIZE = 40; // limit per page


function parsePlayers(playersStr) {
    if (!playersStr) return {cur: 0, max: 0};
    const m = String(playersStr).match(/(\d+)\s*\/\s*(\d+)/);
    return {cur: m ? parseInt(m[1], 10) : 0, max: m ? parseInt(m[2], 10) : 0};
}


// Returns the flag-icons CSS class for a given ISO 3166-1 alpha-2 code.
// Falls back to United Nations flag when code is missing or invalid.
function getFlagIconClass(cc) {
    if (!cc || typeof cc !== 'string') return 'fi-un';
    const raw = cc.trim().toLowerCase();
    const aliases = {uk: 'gb'};
    const code = aliases[raw] || raw;
    if (!/^[a-z]{2}$/.test(code)) return 'fi-un';
    return `fi-${code}`;
}

// ISO 3166-1 alpha-2 country code to country name mapping (subset commonly used by servers)
const COUNTRY_NAMES = {
    AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AR: 'Argentina', AM: 'Armenia', AU: 'Australia', AT: 'Austria',
    AZ: 'Azerbaijan', BH: 'Bahrain', BD: 'Bangladesh', BY: 'Belarus', BE: 'Belgium', BA: 'Bosnia and Herzegovina',
    BR: 'Brazil', BG: 'Bulgaria', CA: 'Canada', CL: 'Chile', CN: 'China', CO: 'Colombia', CR: 'Costa Rica',
    HR: 'Croatia', CY: 'Cyprus', CZ: 'Czechia', DK: 'Denmark', DO: 'Dominican Republic', EC: 'Ecuador', EG: 'Egypt',
    EE: 'Estonia', FI: 'Finland', FR: 'France', GE: 'Georgia', DE: 'Germany', GR: 'Greece', GT: 'Guatemala',
    HU: 'Hungary', IS: 'Iceland', IN: 'India', ID: 'Indonesia', IE: 'Ireland', IL: 'Israel', IT: 'Italy',
    JP: 'Japan', JO: 'Jordan', KZ: 'Kazakhstan', KW: 'Kuwait', LV: 'Latvia', LB: 'Lebanon', LT: 'Lithuania',
    LU: 'Luxembourg', MY: 'Malaysia', MT: 'Malta', MX: 'Mexico', MD: 'Moldova', ME: 'Montenegro', MA: 'Morocco',
    NL: 'Netherlands', NZ: 'New Zealand', MK: 'North Macedonia', NO: 'Norway', PK: 'Pakistan', PA: 'Panama',
    PY: 'Paraguay', PE: 'Peru', PH: 'Philippines', PL: 'Poland', PT: 'Portugal', QA: 'Qatar', RO: 'Romania',
    RU: 'Russia', SA: 'Saudi Arabia', RS: 'Serbia', SG: 'Singapore', SK: 'Slovakia', SI: 'Slovenia', ZA: 'South Africa',
    ES: 'Spain', SE: 'Sweden', CH: 'Switzerland', TH: 'Thailand', TR: 'Türkiye', UA: 'Ukraine', AE: 'United Arab Emirates',
    GB: 'United Kingdom', UK: 'United Kingdom', US: 'United States', UY: 'Uruguay', VN: 'Vietnam',
    XK: 'Kosovo'
};
function getCountryName(code) {
    if (!code) return 'Unknown';
    const cc = String(code).toUpperCase();
    return COUNTRY_NAMES[cc] || cc;
}

function buildQuery(filters) {
    // Build query string for API filters only (no sorting). Text must be URL-encoded.
    // Note: We do NOT use this for sorting; sorting is always in-memory.
    const params = new URLSearchParams();
    if (!filters || typeof filters !== 'object') return '';
    const text = (filters.text ?? '').toString().trim();
    if (text) params.set('text', text);
    if (filters.allow_empty !== undefined) params.set('allow_empty', String(!!filters.allow_empty));
    if (filters.allow_full !== undefined) params.set('allow_full', String(!!filters.allow_full));
    const num = (v) => (v === '' || v == null ? '' : String(Number(v)));
    const minPing = num(filters.min_ping);
    const maxPing = num(filters.max_ping);
    const minPlayers = num(filters.min_players);
    const maxPlayers = num(filters.max_players);
    if (minPing) params.set('min_ping', minPing);
    if (maxPing) params.set('max_ping', maxPing);
    if (minPlayers) params.set('min_players', minPlayers);
    if (maxPlayers) params.set('max_players', maxPlayers);
    // Country filters (tri-state derived into allow/ban lists)
    if (Array.isArray(filters.country_allow) && filters.country_allow.length) {
        params.set('country_allow', filters.country_allow.join(','));
    }
    if (Array.isArray(filters.country_ban) && filters.country_ban.length) {
        params.set('country_ban', filters.country_ban.join(','));
    }
    if (Array.isArray(filters.gamemode) && filters.gamemode.length) {
        params.set('gamemode', filters.gamemode.join(','));
    }
    // Explicitly ignore sort/sort_order to avoid server-side sorting
    return params.toString();
}

function applyLocalFilters(list, filters) {
    const text = (filters.text || '').toString().trim().toLowerCase();
    return list.filter(s => {
        const {cur, max} = parsePlayers(s.players);
        // text search across name, ip, map, and gamemode
        if (text) {
            const hay = `${s.name || ''} ${s.ip || ''} ${s.map || ''} ${s.game_mode || s.gamemode || ''}`.toLowerCase();
            if (!hay.includes(text)) return false;
        }
        // allow_empty / allow_full
        if (filters.allow_empty === false && cur === 0) return false;
        if (filters.allow_full === false && max > 0 && cur >= max) return false;
        // min/max players
        if (filters.min_players != null && filters.min_players !== '' && cur < Number(filters.min_players)) return false;
        if (filters.max_players != null && filters.max_players !== '' && cur > Number(filters.max_players)) return false;
        // ping
        if (filters.min_ping != null && filters.min_ping !== '' && Number(s.ping) < Number(filters.min_ping)) return false;
        if (filters.max_ping != null && filters.max_ping !== '' && Number(s.ping) > Number(filters.max_ping)) return false;
        // country allow/ban
        if (Array.isArray(filters.country_allow) && filters.country_allow.length) {
            if (!filters.country_allow.includes(String(s.country || '').toUpperCase())) return false;
        }
        if (Array.isArray(filters.country_ban) && filters.country_ban.length) {
            if (filters.country_ban.includes(String(s.country || '').toUpperCase())) return false;
        }
        // gamemode (optional contains match)
        if (Array.isArray(filters.gamemode) && filters.gamemode.length) {
            const gm = (s.game_mode || s.gamemode || '').toString();
            const matched = filters.gamemode.some(m => gm.toLowerCase().includes(String(m).toLowerCase()));
            if (!matched) return false;
        }
        return true;
    });
}

function sortLocal(list, filters) {
    const dir = (filters?.sort_order || 'asc').toLowerCase() === 'desc' ? -1 : 1;

    // Custom default sorting when no explicit sort selected
    const customScore = (s) => {
        const { cur, max } = parsePlayers(s.players);
        const ping = Number(s.ping);
        const safePing = Number.isFinite(ping) ? ping : 9999;
        const ratio = (Number(max) > 0) ? (cur / max) : 0; // if no max, treat as 0 filled
        const score = Math.floor(safePing / 15) + (1 - ratio);
        return Number.isFinite(score) ? score : 9999;
    };

    const baseSorted = [...list].sort((a, b) => {
        if (!filters?.sort) {
            const sa = customScore(a);
            const sb = customScore(b);
            return sa === sb ? 0 : (sa < sb ? -1 : 1);
        }
        if (filters.sort === 'ping') {
            const ap = Number(a.ping) || 0;
            const bp = Number(b.ping) || 0;
            return ap === bp ? 0 : (ap < bp ? -1 : 1);
        }
        if (filters.sort === 'players') {
            const {cur: ac} = parsePlayers(a.players);
            const {cur: bc} = parsePlayers(b.players);
            return ac === bc ? 0 : (ac < bc ? -1 : 1);
        }
        return 0;
    });
    return dir === -1 ? baseSorted.reverse() : baseSorted;
}

// Haversine distance in km
function kmBetween(a, b) {
    const [lat1, lon1] = a;
    const [lat2, lon2] = b;
    const R = 6371.0;
    const toRad = (x) => (x * Math.PI) / 180;
    const dlat = toRad(lat2 - lat1);
    const dlon = toRad(lon2 - lon1);
    const la1 = toRad(lat1);
    const la2 = toRad(lat2);
    const a_ = Math.sin(dlat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dlon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a_), Math.sqrt(1 - a_));
    return R * c;
}

function estimatePingMs(distanceKm) {
    // Approximate base latency 10ms + RTT fiber ~ 0.01 ms per km
    return Math.round(6 + distanceKm / 60);
}

const FALLBACK_LOCATION = { latitude: 50.6942, longitude: 3.1746 };

// Geo caching in localStorage for 2 hours
const GEO_CACHE_KEY = 'user_geo_cache_v1';
const GEO_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

async function fetchUserGeo() {
    // Try ipapi.co first
    try {
        const res = await fetch('https://ipapi.co/json/');
        if (res.ok) {
            const j = await res.json();
            const lat = Number(j.latitude);
            const lon = Number(j.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lon)) return { latitude: lat, longitude: lon, source: 'ipapi.co' };
        }
    } catch (_) {}
    // Fallback: ipwho.is
    try {
        const res = await fetch('https://ipwho.is/');
        if (res.ok) {
            const j = await res.json();
            const lat = Number(j.latitude ?? j?.location?.latitude);
            const lon = Number(j.longitude ?? j?.location?.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lon)) return { latitude: lat, longitude: lon, source: 'ipwho.is' };
        }
    } catch (_) {}
    // Fallback: ipinfo.io (may require token; try without)
    try {
        const res = await fetch('https://ipinfo.io/json');
        if (res.ok) {
            const j = await res.json();
            if (typeof j.loc === 'string' && j.loc.includes(',')) {
                const [latS, lonS] = j.loc.split(',');
                const lat = Number(latS);
                const lon = Number(lonS);
                if (Number.isFinite(lat) && Number.isFinite(lon)) return { latitude: lat, longitude: lon, source: 'ipinfo.io' };
            }
        }
    } catch (_) {}
    throw new Error('Geo lookup failed');
}

function getCachedUserGeoNow() {
    try {
        const raw = localStorage.getItem(GEO_CACHE_KEY);
        if (raw) {
            const obj = JSON.parse(raw);
            if (obj && obj.ts && (Date.now() - obj.ts) < GEO_TTL_MS && Number.isFinite(obj.latitude) && Number.isFinite(obj.longitude)) {
                return { latitude: obj.latitude, longitude: obj.longitude, source: obj.source || 'cache' };
            }
        }
    } catch (_) {}
    return null;
}

function setCachedUserGeo(loc) {
    try {
        const payload = { latitude: loc.latitude, longitude: loc.longitude, ts: Date.now(), source: loc.source };
        localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(payload));
    } catch (_) {}
}

function useUserLocation() {
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        const cached = getCachedUserGeoNow();
        if (cached) {
            setLocation(cached);
            setLoading(false);
            return;
        }
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const loc = await fetchUserGeo();
                if (!mounted) return;
                setLocation(loc);
                setCachedUserGeo(loc);
            } catch (e) {
                if (!mounted) return;
                // Don't block UI; leave location null so we use fallback
                setError(e?.message || 'Failed to get location');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    return { location, loading, error };
}

function normalizeServers(rawList, userLoc) {
    const base = (userLoc && Number.isFinite(userLoc.latitude) && Number.isFinite(userLoc.longitude)) ? userLoc : FALLBACK_LOCATION;
    return (rawList || []).map((s, idx) => {
        const lat = Number(s.loc_x ?? s.latitude ?? s.lat);
        const lon = Number(s.loc_y ?? s.longitude ?? s.lon ?? s.lng);
        let ping = null;
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
            const d = kmBetween([base.latitude, base.longitude], [lat, lon]);
            ping = estimatePingMs(d);
        }
        const cur = typeof s.players === 'number' ? s.players : (parseInt(String(s.players || '').split('/')[0], 10) || 0);
        const max = typeof s.max_players === 'number' ? s.max_players : (parseInt(String(s.players || '').split('/')[1], 10) || 0);
        const playersStr = `${cur}/${max || 0}`;
        const country = (s.country || s.cc || '').toString();
        return {
            ...s,
            id: s.id ?? idx,
            players: playersStr,
            ping: ping ?? s.ping ?? null,
            country,
            loc_x: lat,
            loc_y: lon,
        };
    });
}

function useServers(_filters, userLocation, refreshTrigger) {
    const [rawServers, setRawServers] = useState([]);
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch on mount and when refreshTrigger changes (do not refetch on location change)
    useEffect(() => {
        const controller = new AbortController();
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                // Use getServers so Authorization header and refresh-on-401 are handled
                const res = await getServers(controller.signal);
                if (!res.ok) throw new Error('Bad response: ' + res.status);
                const data = await res.json();
                const arr = Array.isArray(data) ? data : (data?.servers ?? []);
                setRawServers(arr);
                setServers(normalizeServers(arr, userLocation));
            } catch (e) {
                if (controller.signal.aborted) return;
                // Fallback to mock JSON from public
                try {
                    const res2 = await fetch('/servser.json', { signal: controller.signal });
                    const data2 = await res2.json();
                    const arr2 = Array.isArray(data2) ? data2 : (data2?.servers ?? []);
                    setRawServers(arr2);
                    setServers(normalizeServers(arr2, userLocation));
                    setError(null);
                } catch (e2) {
                    if (controller.signal.aborted) return;
                    setError(e2.message || 'Failed to load servers');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        load();
        return () => controller.abort();
    }, [refreshTrigger]);

    // Update servers when location changes even if we don't refetch
    useEffect(() => {
        if (!rawServers || !rawServers.length) return;
        setServers(normalizeServers(rawServers, userLocation));
    }, [userLocation?.latitude, userLocation?.longitude]);

    return { servers, loading, error };
}

function Pagination({page, totalPages, onPageChange}) {
    const canPrev = page > 1;
    const canNext = page < totalPages;

    // Render up to 7 page buttons around current page for usability
    const pagesToShow = useMemo(() => {
        const pages = [];
        const start = Math.max(1, page - 3);
        const end = Math.min(totalPages, page + 3);
        for (let p = start; p <= end; p++) pages.push(p);
        return pages;
    }, [page, totalPages]);

    return (<div className="pagination">
            <button disabled={!canPrev} onClick={() => onPageChange(1)}>First</button>
            <button disabled={!canPrev} onClick={() => onPageChange(page - 1)}>Prev</button>
            {pagesToShow.map((p) => (<button key={p} className={p === page ? 'active' : ''} onClick={() => onPageChange(p)}>{p}</button>))}
            <button disabled={!canNext} onClick={() => onPageChange(page + 1)}>Next</button>
            <button disabled={!canNext} onClick={() => onPageChange(totalPages)}>Last</button>
        </div>);
}

export default function ServersTable({ onShowDetails }) {
    // Dynamic country-based filter, options will be derived from loaded servers
    const defaultFilters = {
        text: '', allow_empty: true, allow_full: false, min_ping: '', max_ping: '', min_players: '', max_players: '',
        // tri-state per country: 'neutral' | 'allow' | 'ban'
        countryStates: {}, country_allow: [], country_ban: [], gamemode: [],
        // sorting
        sort: null, // 'ping' | 'players'
        sort_order: 'asc', // 'asc' | 'desc'
    };
    const [draftFilters, setDraftFilters] = useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const refresh = () => setRefreshTrigger(v => v + 1);

    // Auto-refresh every 5 minutes
    useEffect(() => {
        const interval = setInterval(refresh, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const [pingOpen, setPingOpen] = useState(false);
    const [countriesOpen, setCountriesOpen] = useState(false);
    const [searchDraft, setSearchDraft] = useState('');
    const pingRef = useRef(null);
    const countriesRef = useRef(null);

    const PING_OPTIONS = [50, 75, 125, 175, 250];

    const setMaxPing = (val) => {
        setDraftFilters((f) => ({...f, max_ping: val === 0 ? '' : val}));
        setPingOpen(false);
    };

    const getPingLabel = () => {
        const v = draftFilters.max_ping;
        if (v === '' || v == null) return 'Ping: No limit';
        return `Ping: ≤ ${v}ms`;
    };

    const getCountryIndicator = (code) => {
        const state = draftFilters.countryStates[code];
        if (state === 'allow') return {text: '✓', style: {color: '#4CAF50'}};
        if (state === 'ban') return {text: '✕', style: {color: '#f87171'}};
        return {text: '○', style: {}};
    };

    const getCountriesSummary = () => {
        const states = draftFilters.countryStates || {};
        let allow = 0, ban = 0;
        Object.values(states).forEach(v => {
            if (v === 'allow') allow++; else if (v === 'ban') ban++;
        });
        if (!allow && !ban) return 'Countries: All';
        const parts = [];
        if (allow) parts.push(`${allow} allow`);
        if (ban) parts.push(`${ban} ban`);
        return `Countries: ${parts.join(', ')}`;
    };

    // Close ping/countries dropdowns when clicking outside
    useEffect(() => {
        const onDocClick = (e) => {
            if (pingOpen && pingRef.current && !pingRef.current.contains(e.target)) setPingOpen(false);
            if (countriesOpen && countriesRef.current && !countriesRef.current.contains(e.target)) setCountriesOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [pingOpen, countriesOpen]);

    // Close filters modal on Escape
    useEffect(() => {
        if (!filtersOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setFiltersOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [filtersOpen]);


    const { location: userLocation } = useUserLocation();
    const {servers, loading, error} = useServers(appliedFilters, userLocation, refreshTrigger);
    const [page, setPage] = useState(1);

    // Available countries based on loaded servers
    const availableCountries = useMemo(() => {
        const set = new Set();
        for (const s of servers) {
            const cc = String(s.country || '').toUpperCase();
            if (cc && /^[A-Z]{2}$/.test(cc)) set.add(cc);
        }
        return Array.from(set).sort();
    }, [servers]);

    const hasCountryFilters = useMemo(() => {
        const states = draftFilters.countryStates || {};
        return Object.values(states).some(v => v === 'allow' || v === 'ban');
    }, [draftFilters.countryStates]);

    // Apply all filters and sort purely in memory so no network requests are made when changing filters/sort
    const filteredServers = useMemo(() => applyLocalFilters(servers, appliedFilters), [servers, appliedFilters]);
    const sortedServers = useMemo(() => sortLocal(filteredServers, appliedFilters), [filteredServers, appliedFilters.sort, appliedFilters.sort_order]);

    const totalPages = Math.max(1, Math.ceil(sortedServers.length / PAGE_SIZE));

    useEffect(() => {
        // ensure current page exists when data length changes
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    // Reset page when applied filters change
    useEffect(() => {
        setPage(1);
    }, [appliedFilters]);

    // Scroll to top when page changes (scroll inner table area)
    useEffect(() => {
        const scroller = document.querySelector('.servers-grid');
        if (scroller && typeof scroller.scrollTo === 'function') {
            try {
                scroller.scrollTo({ top: 0, behavior: 'instant' });
            } catch (_) {
                scroller.scrollTop = 0;
            }
        } else {
            try {
                window.scrollTo({ top: 0, behavior: 'instant' });
            } catch (_) {
                window.scrollTo(0, 0);
            }
        }
    }, [page]);

    const pageServers = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return sortedServers.slice(start, start + PAGE_SIZE);
    }, [sortedServers, page]);

    const pseudoIp = (s) => `10.0.0.${((s.id ?? 1) % 254) + 1}`;



    const onFieldChange = (e) => {
        const {name, type, value, checked} = e.target;
        setDraftFilters((f) => ({
            ...f, [name]: type === 'checkbox' ? checked : value === '' ? '' : (['min_ping', 'max_ping', 'min_players', 'max_players'].includes(name) ? Number(value) : value)
        }));
    };

    const cycleCountry = (cc) => {
        setDraftFilters((f) => {
            const cur = (f.countryStates && f.countryStates[cc]) || 'neutral';
            const next = cur === 'neutral' ? 'allow' : cur === 'allow' ? 'ban' : 'neutral';
            return {...f, countryStates: {...(f.countryStates || {}), [cc]: next}};
        });
    };

    const toggleGamemode = (mode) => {
        setDraftFilters((f) => {
            const has = f.gamemode.includes(mode);
            return {...f, gamemode: has ? f.gamemode.filter(m => m !== mode) : [...f.gamemode, mode]};
        });
    };

    const clearPingFilter = () => {
        setDraftFilters((f) => ({...f, max_ping: ''}));
    };

    const clearCountries = () => {
        setDraftFilters((f) => ({...f, countryStates: {}, country_allow: [], country_ban: []}));
    };

    const applyFilters = () => {
        // derive country allow/ban lists from tri-state map
        const states = draftFilters.countryStates || {};
        const country_allow = Object.entries(states).filter(([, v]) => v === 'allow').map(([k]) => k);
        const country_ban = Object.entries(states).filter(([, v]) => v === 'ban').map(([k]) => k);
        const next = {...draftFilters, country_allow, country_ban};
        setAppliedFilters(next);
    };

    const toggleSort = (field) => {
        setAppliedFilters((f) => {
            // Cycle: none -> desc -> asc -> none
            if (f.sort !== field) {
                // Activating a new field starts with desc
                return {...f, sort: field, sort_order: 'desc'};
            }
            if (f.sort_order === 'desc') {
                return {...f, sort: field, sort_order: 'asc'};
            }
            // f.sort === field and sort_order === 'asc' (or anything else) => clear sort
            const {sort, sort_order, ...rest} = f;
            return {...rest};
        });
    };

    // Render full layout always; show loading/error only within the table to avoid refreshing the whole page
    return (<div className="container">
            {/* Top toolbar: Search + Filter toggle */}
            <div className="searchbar">
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search in name, IP, map, gamemode"
                    value={searchDraft}
                    onChange={(e) => {
                        const v = e.target.value;
                        setSearchDraft(v);
                        setAppliedFilters((f) => (v === (f.text || '') ? f : {...f, text: v}));
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            setAppliedFilters((f) => ({...f, text: searchDraft}));
                        }
                    }}
                    aria-label="Search servers"
                />
                <button type="button" className={`filter-toggle ${filtersOpen ? 'open' : ''}`} onClick={() => setFiltersOpen(v => !v)}>
                    <i className="fa-solid fa-filter" aria-hidden="true"/> Filter
                </button>
                <button type="button" className="refresh-btn" onClick={refresh} title="Refresh servers" aria-label="Refresh servers">
                    <i className={`fa-solid fa-rotate ${loading ? 'fa-spin' : ''}`} aria-hidden="true"/>
                </button>
            </div>

            {/* Filters Modal */}
            {filtersOpen && (<div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="filters-title" onClick={(e) => {
                    if (e.target === e.currentTarget) setFiltersOpen(false);
                }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-header">
                            <h2 id="filters-title">Filters</h2>
                            <button type="button" className="modal-close" aria-label="Close" onClick={() => setFiltersOpen(false)}>
                                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div id="filters-body" className="filters" role="region" aria-label="Server filters">
                            <label className="filter-item span2">
                                <span>Allow empty</span>
                                <input type="checkbox" className="switch-input" name="allow_empty" checked={!!draftFilters.allow_empty} onChange={onFieldChange}/>
                                <span className="switch-ui" aria-hidden="true"></span>
                            </label>
                            <label className="filter-item span2">
                                <span>Allow full</span>
                                <input type="checkbox" className="switch-input" name="allow_full" checked={!!draftFilters.allow_full} onChange={onFieldChange}/>
                                <span className="switch-ui" aria-hidden="true"></span>

                            </label>
                            <div className="filter-item span2">
                                <span>Ping</span>
                                <div className="inline-control-row">
                                    <div className="dropdown" ref={pingRef}>
                                        <button type="button" className="dropdown-toggle" onClick={() => setPingOpen(v => !v)}>{getPingLabel()}</button>
                                        {pingOpen && (<ul className="dropdown-menu ping-dropdown">
                                                <li className="dropdown-header">
                                                    <small style={{color: '#999', fontSize: 11, width: 300, display: 'block', whiteSpace: 'normal', textAlign: 'justify', marginTop: 4}}>
                                                        The ping value is approximated based on your IP location and the server’s distance. It’s only an estimate, since real ping can vary depending on network routing and other factors.
                                                    </small>
                                                </li>
                                                <li className="divider" style={{marginTop: 4}}/>
                                                {PING_OPTIONS.map((p) => (
                                                    <li key={p}>
                                                        <button type="button" className="dropdown-item" onClick={() => setMaxPing(p)}>
                                                            {p}ms
                                                        </button>
                                                    </li>
                                                ))}
                                                <li>
                                                    <button type="button" className="dropdown-item" onClick={() => setMaxPing(0)}>
                                                        No limit
                                                    </button>
                                                </li>
                                            </ul>)}
                                    </div>
                                    {(draftFilters.max_ping !== '' && draftFilters.max_ping != null) && (
                                        <button type="button" className="reset-btn-small" title="Reset ping filter" aria-label="Reset ping filter" onClick={clearPingFilter}>✕</button>
                                    )}
                                </div>
                            </div>

                            <div className="filter-item span2">
                                <span>Countries</span>
                                <div className="inline-control-row">
                                    <div className="dropdown" ref={countriesRef}>
                                        <button type="button" className="dropdown-toggle" onClick={() => setCountriesOpen(v => !v)}>{getCountriesSummary()}</button>
                                        {countriesOpen && (
                                            <ul className="dropdown-menu regions-dropdown">
                                                {availableCountries.length === 0 && (
                                                    <li className="dropdown-header">No countries available</li>
                                                )}
                                                {availableCountries.map((code) => {
                                                    const ind = getCountryIndicator(code);
                                                    return (
                                                        <li key={code}>
                                                            <button type="button" className="dropdown-item" onClick={(e) => {
                                                                e.stopPropagation();
                                                                cycleCountry(code);
                                                            }}>
                                                                <span className="region-indicator" style={ind.style}>{ind.text}</span>
                                                                <span style={{marginLeft: 8, flex: 1}}>{getCountryName(code)}</span>
                                                                <small style={{opacity: 0.6, marginLeft: 8}}>{code}</small>
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                    {hasCountryFilters && (
                                        <button type="button" className="reset-btn-small" title="Reset country filter" aria-label="Reset country filter" onClick={clearCountries}>✕</button>
                                    )}
                                </div>
                            </div>
                            <div className="filter-item stretch">
                                <span>Gamemode</span>
                                <div className="gamemode-group">
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Public') ? 'active' : ''}`} onClick={() => toggleGamemode('Public')}>Public</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Surf') ? 'active' : ''}`} onClick={() => toggleGamemode('Surf')}>Surf</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('KZ') ? 'active' : ''}`} onClick={() => toggleGamemode('KZ')}>KZ</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Retake') ? 'active' : ''}`} onClick={() => toggleGamemode('Retake')}>Retake</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('AWP') ? 'active' : ''}`} onClick={() => toggleGamemode('AWP')}>AWP</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Jailbreak') ? 'active' : ''}`} onClick={() => toggleGamemode('Jailbreak')}>Jailbreak
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('ZombieMod') ? 'active' : ''}`} onClick={() => toggleGamemode('ZombieMod')}>ZombieMod
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('ZombieEscape') ? 'active' : ''}`}
                                            onClick={() => toggleGamemode('ZombieEscape')}>ZombieEscape
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('BunnyHop') ? 'active' : ''}`} onClick={() => toggleGamemode('BunnyHop')}>BunnyHop
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('DeathMatch') ? 'active' : ''}`} onClick={() => toggleGamemode('DeathMatch')}>DeathMatch
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Arena') ? 'active' : ''}`} onClick={() => toggleGamemode('Arena')}>Arena</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('PropHunt') ? 'active' : ''}`} onClick={() => toggleGamemode('PropHunt')}>PropHunt
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('TTT') ? 'active' : ''}`} onClick={() => toggleGamemode('TTT')}>TTT</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('OneVOne') ? 'active' : ''}`} onClick={() => toggleGamemode('OneVOne')}>1v1</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('GunGame') ? 'active' : ''}`} onClick={() => toggleGamemode('GunGame')}>GunGame</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('FiveVFive') ? 'active' : ''}`} onClick={() => toggleGamemode('FiveVFive')}>5v5</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('CombatSurf') ? 'active' : ''}`} onClick={() => toggleGamemode('CombatSurf')}>CombatSurf
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('HNS') ? 'active' : ''}`} onClick={() => toggleGamemode('HNS')}>HNS</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('OnlyKnife') ? 'active' : ''}`} onClick={() => toggleGamemode('OnlyKnife')}>OnlyKnife
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('DeathRun') ? 'active' : ''}`} onClick={() => toggleGamemode('DeathRun')}>DeathRun
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('MiniGames') ? 'active' : ''}`} onClick={() => toggleGamemode('MiniGames')}>MiniGames
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('AIM') ? 'active' : ''}`} onClick={() => toggleGamemode('AIM')}>AIM</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Duels') ? 'active' : ''}`} onClick={() => toggleGamemode('Duels')}>Duels</button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('FightYard') ? 'active' : ''}`} onClick={() => toggleGamemode('FightYard')}>FightYard
                                    </button>
                                    <button type="button" className={`gm-btn ${draftFilters.gamemode.includes('Warcraft') ? 'active' : ''}`} onClick={() => toggleGamemode('Warcraft')}>Warcraft
                                    </button>

                                </div>
                            </div>
                        </div>
                        <div className="filters-actions modal-actions">
                            <button type="button" className="btn-plain" onClick={() => setFiltersOpen(false)}>Cancel</button>
                            <button type="button" className="filter-apply" onClick={() => {
                                applyFilters();
                                setFiltersOpen(false);
                            }}>Apply Filters
                            </button>
                        </div>
                    </div>
                </div>)}

            <div className="servers-layout">
                <section className="servers-main">
                    <div className="grid-wrapper">
                        <div className="grid-head">
                            <div className="gh-col name">Server Name</div>
                            <div className="gh-col">Gamemode</div>

                            <div style={{textAlign: 'center'}} className={`gh-col th-sortable ${appliedFilters.sort === 'players' ? 'active' : ''}`}>
                                <button type="button" onClick={() => toggleSort('players')} className="th-btn">
                                    Players
                                    <i
                                        className={`fa-solid ${appliedFilters.sort === 'players' ? (appliedFilters.sort_order === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up') : 'fa-up-down'}`}
                                        style={{marginLeft: 6}}
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div className="gh-col col-ip">IP</div>

                            <div className="gh-col" style={{textAlign: 'right'}}>Map</div>
                            <div className={`gh-col right th-sortable ${appliedFilters.sort === 'ping' ? 'active' : ''}`}>
                                <button type="button" onClick={() => toggleSort('ping')} className="th-btn" style={{paddingRight: 12}}>
                                    Ping
                                    <i
                                        className={`fa-solid ${appliedFilters.sort === 'ping' ? (appliedFilters.sort_order === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up') : 'fa-up-down'}`}
                                        style={{marginLeft: 6}}
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Loading / Error states */}
                        {loading && servers.length === 0 && (
                            <div className="grid-state muted">Loading servers…</div>
                        )}
                        {!loading && error && servers.length === 0 && (
                            <div className="grid-state error">Error: {error}</div>
                        )}

                        {/* Rows grid */}
                        <div className="servers-grid">
                            {pageServers.map((s) => {
                                const key = s.id ?? s.name;
                                const ipStr = s.ip ? `${s.ip}${s.port ? `:${s.port}` : ''}` : pseudoIp(s);

                                const connect = () => {
                                    const url = `steam://connect/${ipStr}`;
                                    window.open(url, '_blank');
                                };
                                return (
                                    <React.Fragment key={key}>
                                        <div
                                            className="server-row clickable"
                                            role="link"
                                            tabIndex={0}
                                            onClick={connect}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    connect();
                                                }
                                            }}
                                            style={{'--row-bg': `url("/maps/${encodeURIComponent(s.map || 'map')}.webp")`}}
                                        >
                                            <div className="cell name">
                                                {/*<img src={"https://servers-frontend.fivem.net/api/servers/icon/6r9ob4/138144131.png"} width={32} height={32}*/}
                                                {/*     style={{ marginRight: 10, verticalAlign: "middle"}} alt=""/>*/}
                                                {/*<span className="servers-region">[{s.region_name || s.region || 'Unknown'}] </span>*/}
                                                <span
                                                    className={`fi ${getFlagIconClass(s.country)}`}
                                                    style={{fontSize: 18, marginRight: 8}}
                                                    aria-label={getCountryName(s.country) || 'Unknown'}
                                                />
                                                {' '}{s.name}</div>
                                            <div className="cell" >{s.game_mode}</div>

                                            <div className="cell" style={{textAlign: 'center'}}>{s.players}</div>
                                            <div className="cell col-ip"><button type="button" className="ip-copy" onClick={(e) => { e.stopPropagation(); navigator.clipboard && navigator.clipboard.writeText(ipStr).catch(() => {}); }} title="Click to copy IP" aria-label={`Copy IP ${ipStr}`}><span className="ip-text">{ipStr}</span><i className="fa-regular fa-copy copy-icon" aria-hidden="true" /></button></div>

                                            <div className="cell" style={{textAlign: 'right'}}>{s.map}</div>

                                            <div className="cell right">
                                                <span className={Number(s.ping) < 50 ? 'ping-green' : Number(s.ping) < 100 ? 'ping-yellow' : 'ping-red'}>
                                                    {s.ping}
                                                </span>

                                                {/*<button*/}
                                                {/*    type="button"*/}
                                                {/*    className="btn-plain"*/}
                                                {/*    onClick={(e) => { e.stopPropagation(); onShowDetails && onShowDetails(pseudoIp(s)); }}*/}
                                                {/*    aria-label={`View details for ${s.name}`}*/}
                                                {/*    title={`View details for ${s.name}`}*/}
                                                {/*>*/}
                                                {/*    Details*/}
                                                {/*</button>*/}
                                                <a
                                                    href={`steam://connect/${pseudoIp(s)}`}
                                                    className="connect-btn"
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label={`Connect to ${s.name}`}
                                                    title={`Connect to ${s.name}`}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                                                        <path d="M8 5v14l11-7-11-7z"></path>
                                                    </svg>
                                                    <span className="sr-only">Connect</span>
                                                </a>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* No results */}
                        {!loading && !error && pageServers.length === 0 && (
                            <div className="grid-state">No servers found</div>
                        )}
                    </div>
                    <Pagination page={page} totalPages={totalPages} onPageChange={setPage}/>
                    <div className="meta">Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredServers.length)} of {filteredServers.length}</div>
                </section>
                {/*<FeaturedPanel />*/}
            </div>
        </div>);
}
